import React, { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import PageViewTracker from "./components/PageViewTracker";
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  CircularProgress,
} from "@mui/material";
import HomePage from "./components/HomePage";
import PrivateRoute from "./services/routes/PrivateRoute";
import { NotificationProvider } from "./context/NotificationContext";
import RouteErrorBoundary from "./components/helpers/RouteErrorBoundary";
import V2BootFallback from "./v2admin/theme/BootFallback";
import { classicHashTarget, withoutV2 } from "./adminPaths";
// Robots "noindex, nofollow" while a private page is mounted (admin, client
// portal, login, signup). The server shells and headers say the same
// (scripts/postbuild-pages.js, public/_headers); this covers client-side
// navigation and any host that serves the plain shell.
import { Noindex } from "./v4/utils/useNoindex";
// Loaded before the router mounts so its popstate listener runs first: it
// holds browser Back while the admin has unsaved work (tiny, no admin code).
import "./components/admin/adminLeaveGuard";

// Everything except the landing page is code-split. The admin subtree is the
// big win: ~67% of src/ is admin-only and used to ship to every public
// visitor, dragging in the PDF renderer/viewer, markdown editor, data grid and
// QR libraries. HomePage stays eager so the landing route paints immediately.
const BookingPage = lazy(() => import("./components/Booking"));
const CalendarPage = lazy(() => import("./components/Calendar"));
const AudioVideoPage = lazy(() => import("./components/AudioVideoPage"));
const VideoLinkList = lazy(() =>
  import("./components/admin/video/VideoLinkList")
);
const AdminPage = lazy(() => import("./components/admin/AdminPage"));
// Admin v2 (redesign), the admin at /rdj-api since 2026-10-01.
const AdminV2App = lazy(() => import("./v2admin/AdminV2App"));
const EventList = lazy(() => import("./components/admin/events/EventList"));
const ReviewList = lazy(() => import("./components/admin/reviews/ReviewList"));
const LoginPage = lazy(() => import("./components/auth/LoginPage"));
const SignupPage = lazy(() => import("./components/auth/SignupPage"));
const ReviewsPage = lazy(() => import("./components/ReviewsPage"));
const GalleryPage = lazy(() => import("./components/GalleryPage"));
// Unlisted rate sheet for venue coordinators. Linked from nowhere on purpose.
const VenuesPage = lazy(() => import("./components/VenuesPage"));
const SoundEngineeringServicesPage = lazy(() =>
  import("./components/SoundEngineeringServicesPage")
);
const SingleAudioPage = lazy(() =>
  import("./components/audio/SingleAudioPage")
);
const AvailabilityPage = lazy(() => import("./components/AvailabilityPage"));
const PrivacyPolicyPage = lazy(() =>
  import("./components/legal/PrivacyPolicyPage")
);
const TermsPage = lazy(() => import("./components/legal/TermsPage"));
const InfoExpectProcessPage = lazy(() =>
  import("./components/InfoExpectProcessPage")
);
const SongRequestsPage = lazy(() => import("./components/SongRequestsPage"));
// Target of the permanent printed song request sign. Linked from nowhere.
const LiveRequestsPage = lazy(() => import("./components/LiveRequestsPage"));
// Client portal, opened by the signed link /portal/:eventId?k=. Linked from
// no navigation.
const PortalPage = lazy(() => import("./components/portal/PortalPage"));
// Catch-all for mistyped URLs. Must stay the last route: "*" ranks below
// every other path, so it only renders when nothing else matched.
const NotFoundPage = lazy(() => import("./components/NotFoundPage"));

// Global MUI theme: set Montserrat as the default font family
const theme = createTheme({
  typography: {
    fontFamily:
      '"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
  },
});

const RouteFallback = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "60vh",
      width: "100%",
    }}
  >
    <CircularProgress sx={{ color: "#44A08D" }} />
  </Box>
);

// Public site v4 (djr only). REACT_APP_BRAND is fixed at build time, so the
// RISE MN build drops this whole branch, v4 chunks included (webpack folds
// the constant comparison). RISE MN keeps today's public components.
// The brand config from the DB is not used here on purpose: it can replace
// brand.slug at runtime.
const v4 =
  process.env.REACT_APP_BRAND === "djr"
    ? {
        PublicLayout: lazy(() => import("./v4/layout/PublicLayout")),
        HomePage: lazy(() => import("./v4/pages/HomePage")),
        AvailabilityPage: lazy(() => import("./v4/pages/AvailabilityPage")),
        BookingPage: lazy(() => import("./v4/pages/BookingPage")),
        ReviewsPage: lazy(() => import("./v4/pages/ReviewsPage")),
        GalleryPage: lazy(() => import("./v4/pages/GalleryPage")),
        MixesPage: lazy(() => import("./v4/pages/MixesPage")),
        SoundPage: lazy(() => import("./v4/pages/SoundPage")),
        CorporatePage: lazy(() => import("./v4/pages/CorporatePage")),
        VenuesPage: lazy(() => import("./v4/pages/VenuesPage")),
        HowItWorksPage: lazy(() => import("./v4/pages/HowItWorksPage")),
        NotFoundPage: lazy(() => import("./v4/pages/NotFoundPage")),
        SongRequestsPage: lazy(() => import("./v4/pages/SongRequestsPage")),
        LiveRequestsPage: lazy(() => import("./v4/pages/LiveRequestsPage")),
        WeddingPage: lazy(() => import("./v4/pages/WeddingPage")),
        BirthdayPage: lazy(() => import("./v4/pages/BirthdayPage")),
        HolidayPage: lazy(() => import("./v4/pages/HolidayPage")),
        FaqPage: lazy(() => import("./v4/pages/FaqPage")),
        // Today's /calendar page plus the noindex robots meta.
        CalendarPage: lazy(() => import("./v4/pages/CalendarRoute")),
      }
    : null;

// Quiet stand-in while the v4 shell chunk loads (the v4 background is
// already painted by the pre-paint script, so no spinner).
const V4ShellFallback = () => <Box sx={{ minHeight: "100vh" }} />;

// v4 public pages, all inside PublicLayout (header, menu, footer). The "*"
// NotFound stays the last route.
const v4PublicRoutes = (V) => (
  <Route
    element={
      <Suspense fallback={<V4ShellFallback />}>
        <V.PublicLayout />
      </Suspense>
    }
  >
    <Route path="/" element={<V.HomePage />} />
    <Route path="/availability" element={<V.AvailabilityPage />} />
    <Route path="/booking" element={<V.BookingPage />} />
    <Route path="/reviews" element={<V.ReviewsPage />} />
    <Route path="/gallery" element={<V.GalleryPage />} />
    <Route path="/audio" element={<V.MixesPage />} />
    <Route
      path="/sound-engineering-services"
      element={<V.SoundPage />}
    />
    <Route path="/corporate-events" element={<V.CorporatePage />} />
    <Route path="/wedding-dj" element={<V.WeddingPage />} />
    <Route path="/birthday-party-dj" element={<V.BirthdayPage />} />
    <Route path="/holiday-party-dj" element={<V.HolidayPage />} />
    <Route path="/faq" element={<V.FaqPage />} />
    <Route path="/venues" element={<V.VenuesPage />} />
    <Route path="/info-expect-process" element={<V.HowItWorksPage />} />
    <Route path="/song-requests/:eventId" element={<V.SongRequestsPage />} />
    <Route path="/requests" element={<V.LiveRequestsPage />} />
    <Route path="*" element={<V.NotFoundPage />} />
  </Route>
);

// Today's public pages (RISE MN, and djr before v4).
const legacyPublicRoutes = () => (
  <>
    <Route path="/" element={<HomePage />} />
    <Route path="/info-expect-process" element={<InfoExpectProcessPage />} />
    <Route path="/venues" element={<VenuesPage />} />
    <Route path="/booking" element={<BookingPage />} />
    <Route path="/availability" element={<AvailabilityPage />} />
    <Route path="/audio" element={<AudioVideoPage />} />
    <Route
      path="/sound-engineering-services"
      element={<SoundEngineeringServicesPage />}
    />
    <Route path="/reviews" element={<ReviewsPage />} />
    <Route path="/gallery" element={<GalleryPage />} />
    <Route path="/song-requests/:eventId" element={<SongRequestsPage />} />
    <Route path="/requests" element={<LiveRequestsPage />} />
  </>
);

// Old /rdj-api/v2/... links: the same page without /v2.
const V2LinkRedirect = () => <Navigate to={withoutV2(useLocation())} replace />;

// Old Classic tab links (/rdj-api#gallery-order, /rdj-api?eventId=...#events):
// to Classic at /rdj-api/classic. Everything else is the new admin.
const ClassicHashRedirect = ({ children }) => {
  const target = classicHashTarget(useLocation());
  return target ? <Navigate to={target} replace /> : children;
};

// Wrap the routes with a component to ensure context is properly initialized
const AppRoutes = () => (
  <Routes>
    {v4 ? null : legacyPublicRoutes()}
    <Route path="/login" element={<Noindex><LoginPage /></Noindex>} />
    <Route path="/signup" element={<Noindex><SignupPage /></Noindex>} />
    <Route path="/privacy" element={<PrivacyPolicyPage />} />
    <Route path="/terms" element={<TermsPage />} />
    {/* No v4 board: today's pages and background on both brands. DJ RISE
        adds the noindex robots meta (v4.CalendarPage wraps the same page). */}
    <Route path="/calendar" element={v4 ? <v4.CalendarPage /> : <CalendarPage />} />
    <Route path="/audio/id/:id" element={<SingleAudioPage />} />
    <Route path="/audio/:slug" element={<SingleAudioPage />} />
    <Route path="/portal/:eventId" element={<Noindex><PortalPage /></Noindex>} />
    {/* Admin (src/v2admin): its own login, guard and 404 under /rdj-api.
        Classic admin under /rdj-api/classic until 2026-10-31 (these more
        specific paths rank above /rdj-api/*). Old links: src/adminPaths.js. */}
    <Route path="/rdj-api/v2/*" element={<Noindex><V2LinkRedirect /></Noindex>} />
    <Route
      path="/rdj-api/*"
      element={
        <Noindex>
          <ClassicHashRedirect>
            <Suspense fallback={<V2BootFallback />}>
              <AdminV2App />
            </Suspense>
          </ClassicHashRedirect>
        </Noindex>
      }
    />
    <Route
      path="/rdj-api/classic"
      element={
        <Noindex>
          <PrivateRoute>
            <AdminPage />
          </PrivateRoute>
        </Noindex>
      }
    />
    <Route
      path="/rdj-api/classic/video-links"
      element={
        <Noindex>
          <PrivateRoute>
            <VideoLinkList />
          </PrivateRoute>
        </Noindex>
      }
    />
    <Route
      path="/rdj-api/classic/events"
      element={
        <Noindex>
          <PrivateRoute>
            <EventList />
          </PrivateRoute>
        </Noindex>
      }
    />
    <Route
      path="/rdj-api/classic/reviews"
      element={
        <Noindex>
          <PrivateRoute>
            <ReviewList />
          </PrivateRoute>
        </Noindex>
      }
    />
    <Route
      path="/rdj-api/video-links"
      element={<Noindex><Navigate to="/rdj-api/classic/video-links" replace /></Noindex>}
    />
    {v4 ? v4PublicRoutes(v4) : <Route path="*" element={<NotFoundPage />} />}
  </Routes>
);

function App() {
  return (
    <Router basename={process.env.PUBLIC_URL}>
      <PageViewTracker />
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <NotificationProvider>
          <div className="app">
            <div className="app-main">
              <RouteErrorBoundary>
                <Suspense fallback={<RouteFallback />}>
                  <AppRoutes />
                </Suspense>
              </RouteErrorBoundary>
            </div>
          </div>
        </NotificationProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
